export const MISSING_ORDERS_KEY = 'missing'
export const FLAGGED_ORDERS_KEY = 'flagged'
export const ALL_ORDERS_KEY = 'all'

export const ORDER_TYPES = [MISSING_ORDERS_KEY, FLAGGED_ORDERS_KEY, ALL_ORDERS_KEY] as const

export const FLAGGED_PAGE_SIZE = 50

export const ORDER_STATUS_VALUES = [
  'CARRIER',
  'CONFIRMED',
  'DEAD',
  'DELIVERED',
  // Emitted only by the derived status in nova_orders_view; fz_sales_order never carries it.
  'DISPATCH',
  'FAILED',
  'FLAGGED',
  'HOLD',
  'INVOICED',
  // Emitted only by the derived status in nova_orders_view: invoiced without a DLVD proof of delivery.
  'INVOICED WITH EXCEPTION',
  'MISSING',
  'RECEIVED',
  'RELEASE',
  'SENT',
  'SUBMITTED',
  // Orders held for O2C payment term review. Produced only on the Nova line, so it is excluded
  // from the status filter options elsewhere. The literal carries a space, so never derive an
  // i18n key or a CSS class from the raw value; look the label up in ORDER_STATUS_LABELS.
  'UNDER REVIEW',
] as const

export type OrderStatus = (typeof ORDER_STATUS_VALUES)[number]

/**
 * The only statuses an orders view can emit: the ones passed through from `fz_sales_order` plus the
 * ones derived from the order-to-cash timestamps.
 *
 * Screens reading the derived status should offer only these as filter options, since anything else
 * matches nothing. Screens reading the raw `fz_sales_order.status` must NOT use this list:
 * mainline's `returns_view` selects it unchanged, so legacy values like SENT and RELEASE are real
 * there. Returns are therefore split: Nova serves them from `nova_returns_view`, which derives the
 * status and needs this list, while mainline keeps the raw column and the full one.
 *
 * DISPATCH, INVOICED WITH EXCEPTION and UNDER REVIEW are produced by `nova_orders_view` alone, so this list is the Nova
 * vocabulary. `NOVA_ONLY_ORDER_STATUS_VALUES` below names them, and the filter drops them where
 * they cannot match.
 */
export const DERIVED_ORDER_STATUS_VALUES = [
  'CARRIER',
  'CONFIRMED',
  'DEAD',
  'DELIVERED',
  'DISPATCH',
  'FAILED',
  'HOLD',
  'INVOICED',
  'INVOICED WITH EXCEPTION',
  'RECEIVED',
  'SUBMITTED',
  'UNDER REVIEW',
] as const satisfies readonly OrderStatus[]

/**
 * Statuses only a Nova database can produce. Offering one elsewhere is a filter option that can
 * never match a row, so the order-status filter drops them when the environment is not Nova.
 */
export const NOVA_ONLY_ORDER_STATUS_VALUES = [
  'DISPATCH',
  'INVOICED WITH EXCEPTION',
  'UNDER REVIEW',
] as const satisfies readonly OrderStatus[]

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  CARRIER: 'orderStatus.carrier',
  CONFIRMED: 'orderStatus.confirmed',
  DEAD: 'orderStatus.dead',
  DELIVERED: 'orderStatus.delivered',
  DISPATCH: 'orderStatus.dispatch',
  FAILED: 'orderStatus.failed',
  FLAGGED: 'orderStatus.flagged',
  HOLD: 'orderStatus.hold',
  INVOICED: 'orderStatus.invoiced',
  'INVOICED WITH EXCEPTION': 'orderStatus.invoicedWithException',
  MISSING: 'orderStatus.missing',
  RECEIVED: 'orderStatus.received',
  RELEASE: 'orderStatus.release',
  SENT: 'orderStatus.sent',
  SUBMITTED: 'orderStatus.submitted',
  'UNDER REVIEW': 'orderStatus.underReview',
}

/**
 * How an order's two legs travel. Derived rather than stored: `nova_orders_view` computes it, and
 * the order-details path derives the same value from the order-to-cash row and the account's
 * fulfillment method. Only the Nova line produces it.
 */
export const COUPLING_STATE_VALUES = ['Coupled', 'Decoupled', 'Single-leg'] as const

export type CouplingState = (typeof COUPLING_STATE_VALUES)[number]

export const COUPLING_STATE_LABELS: Record<CouplingState, string> = {
  Coupled: 'orderDataTable.couplingState.coupled',
  Decoupled: 'orderDataTable.couplingState.decoupled',
  'Single-leg': 'orderDataTable.couplingState.singleLeg',
}

/**
 * The payment term review decision on an order flagged for review. Inferred rather than stored:
 * `nova_orders_view.ptr_status` derives it from the raw status and the leg's order-to-cash
 * submission, and orders never flagged for review carry none. It sits beside the order status and
 * never replaces it. Only the Nova line produces it.
 */
export const PTR_STATUS_VALUES = ['UNDER REVIEW', 'RELEASED', 'CANCELLED'] as const

export type PtrStatus = (typeof PTR_STATUS_VALUES)[number]

export const PTR_STATUS_LABELS: Record<PtrStatus, string> = {
  'UNDER REVIEW': 'ptrStatus.underReview',
  RELEASED: 'ptrStatus.released',
  CANCELLED: 'ptrStatus.cancelled',
}

/**
 * Whether a reviewer moved an order's payment term. CHANGED only when a modified term is set, is not
 * blank, and differs from the term the order arrived with; anything else is UNCHANGED. Derived in the
 * Nova list query, and kept apart from the PTR status: a changed term is still under review.
 */
export const TERM_CHANGE_VALUES = ['CHANGED', 'UNCHANGED'] as const

export type TermChange = (typeof TERM_CHANGE_VALUES)[number]

export const TERM_CHANGE_LABELS: Record<TermChange, string> = {
  CHANGED: 'termChange.changed',
  UNCHANGED: 'termChange.unchanged',
}

/**
 * SOURCES
 */
export const SOURCE_RMA = 'RMA'

/**
 * Orders raised in SAP. Produced on the Nova line alone: prod carries EDI, SF, INJ, NOD and RMA and
 * no SAP row at all, so it is offered as a filter option only where it can match something.
 */
export const SOURCE_SAP = 'SAP'

/**
 * The four sources the welcome dashboard breaks out into its own chart series. SAP is deliberately
 * not one of them: the charts carry a fixed series per source, so adding it here would add a series
 * rather than a filter option.
 */
export const SOURCES_WO_RMA = ['EDI', 'INJ', 'NOD', 'SF'] as const

export const SOURCE_VALUES = [...SOURCES_WO_RMA, SOURCE_SAP, SOURCE_RMA] as const

/**
 * Sources only a Nova database can produce, named the same way `NOVA_ONLY_ORDER_STATUS_VALUES` names
 * its statuses. Offering one elsewhere is a filter option that can never match a row.
 */
export const NOVA_ONLY_SOURCE_VALUES = [SOURCE_SAP] as const

export type Source = (typeof SOURCE_VALUES)[number]

export type SourceWoRma = (typeof SOURCES_WO_RMA)[number]

export const SOURCE_KEYS_WO_RMA = ['EDI', 'INJ', 'NOD', 'SF'] as const

export const SOURCE_KEYS = [...SOURCE_KEYS_WO_RMA, SOURCE_SAP, SOURCE_RMA] as const

export type SourceKey = (typeof SOURCE_KEYS)[number]

export type SourceKeyWoRma = (typeof SOURCE_KEYS_WO_RMA)[number]

export type LowercaseSourceKeyWoRma = Lowercase<SourceKeyWoRma>

export const SOURCE_LABELS: Record<Source, SourceKey> = {
  EDI: 'EDI',
  INJ: 'INJ',
  NOD: 'NOD',
  SF: 'SF',
  SAP: 'SAP',
  RMA: 'RMA',
}

/**
 * OFF ROUTE
 */
export const OFF_ROUTE_VALUES = ['offRoute'] as const

export type OffRouteOption = (typeof OFF_ROUTE_VALUES)[number]

/**
 * ITEMS
 */
export const MATERIAL_GROUPS = {
  fmc: ['ZFG100000'],
  vuse: ['ZFG431000', 'ZFG439003', 'ZFG439006'],
}

export const MATERIAL_LABELS = {
  fmc: 'FMC',
  vuse: 'Vuse',
  other: 'Other',
} as const

/**
 * JOURNEY
 */
export const JOURNEY_STEPS = [
  'RECEIVED',
  'SUBMITTED',
  'CONFIRMED',
  'CARRIER',
  'ONROUTE',
  'INVOICED',
] as const

export type JourneyStep = (typeof JOURNEY_STEPS)[number]

export const JOURNEY_STEPS_LABELS: Record<JourneyStep, string> = {
  RECEIVED: 'journeyStepLabel.received',
  SUBMITTED: 'journeyStepLabel.submitted',
  CONFIRMED: 'journeyStepLabel.confirmed',
  CARRIER: 'journeyStepLabel.carrier',
  ONROUTE: 'journeyStepLabel.onroute',
  INVOICED: 'journeyStepLabel.invoiced',
}

export const getJourneyStepLabel = (step: JourneyStep) => JOURNEY_STEPS_LABELS[step]

export type JourneyStepWithDocument = Exclude<JourneyStep, 'RECEIVED' | 'SUBMITTED'>

const DOCUMENT_NUMBER_LABELS: Record<JourneyStepWithDocument, string> = {
  CONFIRMED: 'documentNumberLabel.sapOrderNumber',
  CARRIER: 'documentNumberLabel.sapDeliveryNumber',
  ONROUTE: 'documentNumberLabel.sapDeliveryNumber',
  INVOICED: 'documentNumberLabel.sapInvoiceNumber',
}

export const getDocumentNumberLabel = (step: JourneyStepWithDocument) =>
  DOCUMENT_NUMBER_LABELS[step] ?? 'documentNumberLabel.generic'

/**
 * SHPORD LEG-RELEASE CUTOFF RULES
 *
 * The base rule names used when updating a SHPORD cutoff rule (POST). Shared by
 * the frontend forms and the backend validation/helper so the set stays in sync.
 * The Nova GET read-contract uses scoped variants (e.g. `_ON`/`_OFF`) that are
 * backend-only and intentionally not listed here.
 */
export const SHPORD_CUTOFF_RULES = [
  'RULE_GLOBAL_DECOUPLING',
  'RULE_NON_EDI_CUTOFF',
  'RULE_EDI_CUTOFF',
] as const

export type CutoffRule = (typeof SHPORD_CUTOFF_RULES)[number]

/**
 * The distribution centres an EDI or non-EDI cutoff is set for. Each takes its own cutoff, entered
 * in the DC's local time; Nova holds the DC → timezone mapping and does the UTC conversion.
 */
export const SHPORD_CUTOFF_DC_CODES = ['CA41', 'CA43', 'CA48'] as const

export type ShpordCutoffDcCode = (typeof SHPORD_CUTOFF_DC_CODES)[number]
